// src/context/LanguageContext.jsx
import React, { createContext, useContext, useState, useCallback } from 'react';
import { translations } from '../../json/data';

const LanguageContext = createContext(null);

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', display: 'EN' },
  { code: 'enhi', label: 'Hinglish', display: 'HE' },
  { code: 'hi', label: 'हिंदी', display: 'HI' },
  { code: 'ta', label: 'தமிழ்', display: 'TA' },
  { code: 'mr', label: 'मराठी', display: 'MA' },
];

const getStorageKey = () => {
  const path = window.location.pathname.split("/").filter(Boolean);
  const seg1 = path[0] ? decodeURIComponent(path[0]) : "default";
  const seg2 = path[1] ? decodeURIComponent(path[1]) : "guest";
  return `userLang_${seg1}_${seg2}`;
};

// ✅ Remove all userLang_* keys except current one
const clearOtherLangKeys = () => {
  const currentKey = getStorageKey();
  Object.keys(sessionStorage).forEach(key => {
    if (key.startsWith('userLang_') && key !== currentKey) {
      sessionStorage.removeItem(key);
    }
  });
};

// ✅ Safe sessionStorage wrapper with memory fallback
const memoryFallback = {};
const safeStorage = {
  getItem(key) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return memoryFallback[key] ?? null;
    }
  },
  setItem(key, value) {
    try {
      sessionStorage.setItem(key, value);
      memoryFallback[key] = value;
    } catch {
      memoryFallback[key] = value;
    }
  },
  removeItem(key) {
    try {
      sessionStorage.removeItem(key);
    } catch { }
    delete memoryFallback[key];
  },
  keys() {
    try {
      return Object.keys(sessionStorage);
    } catch {
      return Object.keys(memoryFallback);
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [lanText, setLanText] = useState(() => {
    const key = getStorageKey();
    const stored = safeStorage.getItem(key);
    return (stored && translations[stored]) ? stored : 'en';
  });

  const t = translations[lanText] || translations['en'];

  const setApiLanguage = useCallback((apiLan) => {
    const key = getStorageKey();
    const stored = safeStorage.getItem(key);
    
    // ✅ If user manually picked something other than 'en', keep it
    // But if stored is 'en' (default/auto), allow API to override
    const userManuallyPicked = stored && translations[stored] && stored !== 'en';
    
    if (userManuallyPicked) {
      setLanText(stored);
    } else {
      clearOtherLangKeys();
      if (apiLan && translations[apiLan]) {
        safeStorage.setItem(key, apiLan);
        setLanText(apiLan);
      }
    }
  }, []);

  const setUserLanguage = useCallback((code) => {
    if (!translations[code]) return;
    clearOtherLangKeys();                    // ✅ remove other link keys first
    safeStorage.setItem(getStorageKey(), code);
    setLanText(code);
  }, []);

  const resetToApiLanguage = useCallback((apiLan) => {
    safeStorage.removeItem(getStorageKey());
    if (apiLan && translations[apiLan]) setLanText(apiLan);
  }, []);

  return (
    <LanguageContext.Provider value={{ lanText, t, setApiLanguage, setUserLanguage, resetToApiLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside <LanguageProvider>');
  return ctx;
};

export default LanguageContext;